import { moonUpperLimbAltitude } from './moonPosition';

/** Offset of `timeZone` from UTC at instant `t`, in ms (wall clock minus UTC). */
function zoneOffsetMs(t: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(t));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const wall = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  return wall - Math.floor(t / 1000) * 1000;
}

/**
 * The instant local midnight starts `year-month-day` in `timeZone`. Checked
 * twice so a DST change that day still lands on the right hour. Falls back to
 * UTC midnight for an unknown zone.
 */
export function localMidnight(year: number, month: number, day: number, timeZone: string): Date {
  const utcMidnight = Date.UTC(year, month, day);
  try {
    const first = utcMidnight - zoneOffsetMs(utcMidnight, timeZone);
    return new Date(utcMidnight - zoneOffsetMs(first, timeZone));
  } catch {
    return new Date(utcMidnight);
  }
}

/** Same shape as SunCalc.getMoonTimes. */
export type MoonTimes = { rise?: Date; set?: Date; alwaysUp?: true; alwaysDown?: true };

export type MoonCrossing = { type: 'rise' | 'set'; t: Date };

const SCAN_MS = 20 * 60 * 1000;

/**
 * Every moonrise and moonset between two instants, in order (ERI-262). Scans the
 * moon's top edge every 20 minutes and narrows each horizon crossing to a
 * second. A peek above the horizon shorter than the scan step, which only
 * happens at high latitudes, can be missed.
 */
export function moonCrossings(start: Date, end: Date, latitude: number, longitude: number): MoonCrossing[] {
  const alt = (ms: number) => moonUpperLimbAltitude(new Date(ms), latitude, longitude);
  const out: MoonCrossing[] = [];
  const t1 = end.getTime();
  let a = start.getTime();
  let below = alt(a) < 0;
  while (a < t1) {
    const b = Math.min(a + SCAN_MS, t1);
    const bBelow = alt(b) < 0;
    if (bBelow !== below) {
      let lo = a;
      let hi = b;
      while (hi - lo > 1000) {
        const mid = (lo + hi) / 2;
        if ((alt(mid) < 0) === below) lo = mid;
        else hi = mid;
      }
      out.push({ type: below ? 'rise' : 'set', t: new Date(Math.round((lo + hi) / 2)) });
    }
    a = b;
    below = bBelow;
  }
  return out;
}

/**
 * Moonrise and moonset on one calendar day at the location (ERI-2380, ERI-262).
 *
 * Searches the 24 hours from the location's own local midnight, so a browser
 * and the server give the same answer. Rise and set are when the moon's top
 * edge crosses a flat horizon, from the accurate position in moonPosition.
 *
 * `month` is 0-based, like Date.
 */
export function moonTimesForLocalDay(
  year: number,
  month: number,
  day: number,
  latitude: number,
  longitude: number,
  timeZone: string,
): MoonTimes {
  const t = localMidnight(year, month, day, timeZone);
  const crossings = moonCrossings(t, new Date(t.getTime() + 24 * 3600 * 1000), latitude, longitude);
  const result: MoonTimes = {};
  const rise = crossings.find((c) => c.type === 'rise');
  const set = crossings.find((c) => c.type === 'set');
  if (rise) result.rise = rise.t;
  if (set) result.set = set.t;
  if (!rise && !set) {
    if (moonUpperLimbAltitude(t, latitude, longitude) >= 0) result.alwaysUp = true;
    else result.alwaysDown = true;
  }
  return result;
}

/**
 * Minutes between two instants when the moon is below the horizon (ERI-262).
 * Over the core's dark window this is the night's dark core time. Uses the same
 * rise/set definition as the moonrise and moonset shown next to it.
 */
export function moonDownMinutes(start: Date, end: Date, latitude: number, longitude: number): number {
  const t0 = start.getTime();
  const t1 = end.getTime();
  if (!(t1 > t0)) return 0;
  let down = moonUpperLimbAltitude(start, latitude, longitude) < 0;
  let from = t0;
  let total = 0;
  for (const c of moonCrossings(start, end, latitude, longitude)) {
    if (down) total += c.t.getTime() - from;
    down = c.type === 'set';
    from = c.t.getTime();
  }
  if (down) total += t1 - from;
  return Math.round(total / 60000);
}
